import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import useHideHubSpotChat from 'hooks/useHideHubSpotChat';
import Spinner from 'components/UI/V3/Spinner';
import BelongingsPrintTable from '../BelongingsPrintTable/BelongingsPrintTable';
import styles from './BelongingsPrintPreview.module.scss';

interface BelongingsPrintPreviewProps {
    items: Array<any>;
    dateLabel: string;
    onClose: () => void;
}

function usePrint(onClose: () => void, isReadyToPrint: boolean) {
    useHideHubSpotChat(true);

    useEffect(() => {
        if (!isReadyToPrint) return;

        document.body.classList.add('printing');

        const timer = setTimeout(() => window.print(), 50);

        const handleAfterPrint = () => {
            document.body.classList.remove('printing');
            onClose();
        };

        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('afterprint', handleAfterPrint);
            document.body.classList.remove('printing');
        };
    }, [onClose, isReadyToPrint]);

    useHideHubSpotChat(false);
}

export const BelongingsPrintPreview: React.FC<BelongingsPrintPreviewProps> = ({ items, dateLabel, onClose }) => {
    const [isReadyToPrint, setIsReadyToPrint] = useState(false);

    usePrint(onClose, isReadyToPrint);

    const groups = useMemo(() => {
        const map = new Map<string, Array<any>>();
        for (const item of items) {
            const group = map.get(item.locationName) ?? [];
            group.push(item);
            map.set(item.locationName, group);
        }
        return Array.from(map.entries()).map(([locationName, groupItems]) => ({
            locationName,
            items: groupItems
        }));
    }, [items]);

    return ReactDOM.createPortal(
        <div className={styles.printOverlay}>
            <div className={styles.printContent}>
                {!isReadyToPrint ? (
                    <div className={styles.spinnerOverlay}>
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <div className={styles.printHeader}>
                            <span className={styles.title}>Belongings · </span>
                            <span className={styles.date}>{dateLabel}</span>
                        </div>
                        <BelongingsPrintTable groups={groups} />
                    </>
                )}
            </div>
            <div className={styles.printFooter}>
                <img
                    className={styles.footerLogo}
                    src="/images/logo/poweredByCuddles.png"
                    onLoad={() => setIsReadyToPrint(true)}
                />
            </div>
        </div>,
        document.body
    );
};

export default BelongingsPrintPreview;
